'use client';

import React, { useEffect, useRef } from 'react';

const testimonial = {
  quote:
    "EarnestBridge funded our $350K EMD in under 40 hours. We had two other deals in escrow at the same time — there's no way we could have done that without them.",
  name: 'Marcus Delacroix',
  title: 'Managing Partner',
  stat: '$350K EMD · 40hr funding',
};

export default function TestimonialSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const el = ref.current;
          if (el) {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="testimonials"
      className="py-24 px-6 relative z-10"
      style={{ background: 'var(--color-bg-alt)' }}
      aria-label="Customer testimonials and success stories"
    >
      {/* Top rule */}
      <div
        className="max-w-5xl mx-auto mb-16"
        style={{ borderTop: '1px solid rgba(27,67,50,0.1)' }}
      />

      <div className="max-w-5xl mx-auto">
        <div
          ref={ref}
          className="text-center"
          style={{
            opacity: 0,
            transform: 'translateY(32px)',
            transition: 'all 1s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {/* Quote mark — editorial, restrained */}
          <div
            className="font-display leading-none mb-8 select-none"
            style={{
              fontSize: '5rem',
              color: 'rgba(82,183,136,0.18)',
              lineHeight: 1,
            }}
            aria-hidden="true"
          >
            &ldquo;
          </div>

          <blockquote
            className="font-display italic mb-12 max-w-3xl mx-auto"
            style={{
              fontSize: 'clamp(1.25rem, 2.5vw, 1.875rem)',
              color: 'var(--color-foreground)',
              lineHeight: 1.45,
              fontWeight: 300,
            }}
          >
            {testimonial?.quote}
          </blockquote>

          {/* Attribution — structured, no pill */}
          <div className="flex items-center justify-center gap-6">
            <div
              className="w-10 h-px"
              style={{ background: 'rgba(201,168,76,0.4)' }}
            />
            <div className="text-center">
              <p
                className="text-[11px] font-bold uppercase tracking-[0.35em]"
                style={{ color: 'var(--color-foreground)' }}
              >
                {testimonial?.name}
              </p>
              <p
                className="text-[10px] font-medium uppercase tracking-[0.25em] mt-1"
                style={{ color: 'var(--color-muted)' }}
              >
                {testimonial?.title}
              </p>
            </div>
            <div
              className="w-10 h-px"
              style={{ background: 'rgba(201,168,76,0.4)' }}
            />
          </div>

          {/* Stat — plain text, no pill */}
          <div className="mt-8">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.3em]"
              style={{ color: 'var(--color-primary)' }}
            >
              {testimonial?.stat}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom rule */}
      <div
        className="max-w-5xl mx-auto mt-16"
        style={{ borderBottom: '1px solid rgba(27,67,50,0.1)' }}
      />
    </section>
  );
}